import { ComponentPropsWithoutRef, ReactNode, useMemo, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { Tag } from '@givebutter/primitives/tag';
import { useToast } from '@givebutter/primitives/toast';
import {
    faCheck,
    faHashtag,
    faMerge,
    faSpinnerThird,
    faStar,
} from '@fortawesome/pro-regular-svg-icons';
import { faSoap, faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { mergeContact } from 'api/contacts/contacts';
import { fetchDataHygieneDuplicates, ignoreDataHygieneDuplicate } from 'api/contacts/data-hygiene';
import Content from 'assets/js/components/Content';
import * as FauxTableEmpty from 'assets/js/pages/contacts/data-hygiene/FauxTableEmpty';
import * as IgnoreButton from 'assets/js/pages/contacts/data-hygiene/IgnoreButton';
import { SuccessStateWithUndo } from 'assets/js/pages/contacts/data-hygiene/SuccessStateWithUndo';
import * as Form from 'components/form';
import { AddressReadable } from 'components/pages/campaign-settings/campaign-auction/AddressReadable';
import { EmptyData } from 'components/util';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { AnimatePresence, motion } from 'framer-motion';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twMerge } from 'tailwind-merge';
import { ContactDuplicate, ContactDuplicate as IContactDuplicate } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const ContactDuplicates = () => {
    const { accountId } = useCurrentAccount();

    const { data: duplicates } = useSuspenseQuery({
        queryKey: accountQueryKeys.dataHygieneDuplicates(accountId),
        queryFn: () => fetchDataHygieneDuplicates(accountId),
    });

    return (
        <div className={'flex flex-col gap-8'}>
            <Content className="max-w-full">
                {duplicates?.data?.length > 0 ? (
                    <div className={'flex flex-col gap-4'}>
                        {duplicates?.data.map(duplicate => (
                            <ContactDuplicatePair
                                key={`${duplicate[0].id}-${duplicate[1].id}`}
                                pairId={`${duplicate[0].id}-${duplicate[1].id}`}
                                contactOne={duplicate[0]}
                                contactTwo={duplicate[1]}
                            />
                        ))}
                    </div>
                ) : (
                    <Box className={'min-w-60'}>
                        <FauxTableEmpty.FauxEmpty>
                            <FauxTableEmpty.EmptyIcon>
                                <FontAwesomeIcon icon={faSoap} />
                            </FauxTableEmpty.EmptyIcon>
                            <FauxTableEmpty.EmptyTitle>Looks clean!</FauxTableEmpty.EmptyTitle>
                            <FauxTableEmpty.EmptyDescription>
                                No duplicate contacts found.
                            </FauxTableEmpty.EmptyDescription>
                        </FauxTableEmpty.FauxEmpty>
                    </Box>
                )}
            </Content>
        </div>
    );
};

interface ContactDuplicatePairProps {
    contactOne: IContactDuplicate;
    contactTwo: IContactDuplicate;
    pairId: string;
}

const columnHelper = createColumnHelper<ContactDuplicate>();

interface MergeFormValues {
    contactId: string;
}

const ContactDuplicatePair = (props: ContactDuplicatePairProps) => {
    const { contactOne, contactTwo, pairId } = props;
    const { accountId } = useCurrentAccount();
    const { error } = useToast();
    const tableData = useMemo(() => [contactOne, contactTwo], [contactOne, contactTwo]);
    const [isMerged, setIsMerged] = useState(false);
    const [isIgnored, setIsIgnored] = useState(false);
    const isSuccess = useMemo(() => isMerged || isIgnored, [isMerged, isIgnored]);
    const successMessage = useMemo(() => {
        if (isMerged) {
            return 'Successfully merged contact';
        } else if (isIgnored) {
            return 'Successfully ignored duplicate';
        }
    }, [isIgnored, isMerged]);

    const { mutate: submit, isPending: mergeIsPending } = useMutation({
        mutationFn: (values: MergeFormValues) => {
            const intoContact = parseInt(values.contactId);
            const fromContact = intoContact === contactOne.id ? contactTwo.id : contactOne.id;

            return mergeContact(accountId, fromContact, intoContact);
        },
        onSuccess: () => {
            setIsMerged(true);
            setIsIgnored(false);
        },
        onError(e: GivebutterAxiosError) {
            error(e.response?.data?.message ?? 'Unable to merge contact.');
        },
    });

    const { mutate: ignoreDuplicate, isPending: ignoreIsPending } = useMutation({
        mutationFn: () => ignoreDataHygieneDuplicate(accountId, contactOne.id),
        onSuccess: () => {
            setIsMerged(false);
            setIsIgnored(true);
        },
        onError(e: GivebutterAxiosError) {
            error(e.response?.data?.message ?? 'Unable to ignore duplicate contact.');
        },
    });

    const methods = Form.useForm<MergeFormValues>({
        defaultValues: {
            contactId: props.contactOne.id.toString(),
        },
    });

    const { control, path, handleSubmit } = methods;

    const columns = useMemo(
        () => [
            columnHelper.accessor('first_name', {
                header: 'First name',
                cell: info => <p>{info.getValue() ?? <EmptyData />}</p>,
            }),
            columnHelper.accessor('last_name', {
                header: 'Last name',
                cell: info => <p>{info.getValue() ?? <EmptyData />}</p>,
            }),
            columnHelper.accessor('dob', {
                header: 'Birthday',
                cell: info => {
                    const dob = info.getValue();

                    if (!dob) {
                        return <EmptyData />;
                    }

                    return isoDateFromServer(dob).toLocaleString(DateTime.DATE_MED);
                },
            }),
            columnHelper.accessor('emails', {
                header: 'Emails',
                cell: info => {
                    const emails = info.getValue();

                    if (!emails) {
                        return <EmptyData />;
                    }

                    return (
                        <div className={'flex flex-col'}>
                            {emails.map(email => (
                                <p key={`${email.type}-${email.value}`}>{email.value}</p>
                            ))}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('phones', {
                header: 'Phones',
                cell: info => {
                    const phones = info.getValue();

                    if (!phones) {
                        return <EmptyData />;
                    }

                    return (
                        <div className={'flex flex-col'}>
                            {phones.map(email => (
                                <p key={`${email.type}-${email.value}`}>{email.value}</p>
                            ))}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('addresses', {
                header: 'Addresses',
                cell: info => {
                    const addresses = info.getValue();

                    if (!addresses) {
                        return <EmptyData />;
                    }

                    return (
                        <div className={'flex flex-col'}>
                            {addresses.map(address => (
                                <AddressReadable
                                    key={address.id}
                                    className={`[&>*]:text-base`}
                                    address={address}
                                />
                            ))}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('tags', {
                header: 'Tags',
                cell: info => {
                    const tags = info.getValue();

                    if (!tags) {
                        return <EmptyData />;
                    }

                    return (
                        <div className={'flex flex-wrap gap-1.5'}>
                            {tags.map(tag => (
                                <Tag key={tag} size="sm">
                                    <FontAwesomeIcon
                                        icon={faHashtag}
                                        size="xs"
                                        className="text-gray-400"
                                    />
                                    {tag}
                                </Tag>
                            ))}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('created_at', {
                header: 'Contact since',
                cell: info => isoDateFromServer(info.getValue()).toLocaleString(DateTime.DATE_MED),
            }),
        ],
        [],
    );

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableHiding: false,
        enableColumnPinning: false,
    });

    return (
        <Form.Provider {...methods}>
            <Box className={'min-w-96 overflow-hidden p-4'}>
                <AnimatePresence initial={false}>
                    <motion.div
                        key={isSuccess ? `${pairId}_success` : pairId}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: isSuccess ? 0.3 : 0.5 }}
                    >
                        {isSuccess ? (
                            <SuccessStateWithUndo
                                successMessage={successMessage}
                                // NOTE: undo is not currently possible for the duplicates endpoints
                                showUndo={false}
                                invalidationKeys={[accountQueryKeys.dataHygiene(accountId)]}
                            >
                                {isMerged ? (
                                    <Button
                                        variant="solid"
                                        color="success"
                                        size="sm"
                                        className="pointer-events-none"
                                    >
                                        <FontAwesomeIcon icon={faCheck} />
                                        Merged
                                    </Button>
                                ) : null}
                            </SuccessStateWithUndo>
                        ) : (
                            <form
                                onSubmit={handleSubmit(submit as SubmitHandler<MergeFormValues>)}
                                className={'contents'}
                            >
                                <div className={'flex flex-col gap-4'}>
                                    <DuplicateColumnContainer>
                                        <div className={'flex gap-1'}>
                                            <Form.Label
                                                name={path('contactId')}
                                                className={'basis-1/3 text-base font-medium'}
                                                htmlFor={'contactId'}
                                            >
                                                Primary record
                                            </Form.Label>
                                            <Form.UseFormRadioGroup
                                                control={control}
                                                name={'contactId'}
                                                variant={'custom'}
                                                className={'flex grow items-center gap-3'}
                                            >
                                                {[
                                                    contactOne.id.toString(),
                                                    contactTwo.id.toString(),
                                                ].map(contact_id => (
                                                    <Form.Label
                                                        key={contact_id}
                                                        name={path('contactId')}
                                                        className={
                                                            'flex flex-1 items-center gap-2 text-base font-normal'
                                                        }
                                                        htmlFor={contact_id}
                                                    >
                                                        <RadioGroup.Item
                                                            value={contact_id}
                                                            className={
                                                                'group/button flex items-center justify-center'
                                                            }
                                                        >
                                                            <RadioGroup.Indicator className="h-5 w-5 bg-transparent">
                                                                <FontAwesomeIcon
                                                                    icon={faStarSolid}
                                                                    className={
                                                                        'text-brand-butter-primary h-auto w-full'
                                                                    }
                                                                />
                                                            </RadioGroup.Indicator>

                                                            <span className="h-5 w-5 group-data-[state=checked]/button:hidden">
                                                                <FontAwesomeIcon
                                                                    icon={faStar}
                                                                    className={
                                                                        'hover:text-brand-butter-primary h-auto w-full text-gray-300'
                                                                    }
                                                                />
                                                            </span>
                                                        </RadioGroup.Item>
                                                    </Form.Label>
                                                ))}
                                            </Form.UseFormRadioGroup>
                                        </div>
                                        {table.getAllColumns().map(col => {
                                            const cellFromCol1 = table
                                                .getRowModel()
                                                .rows[0].getVisibleCells()
                                                .find(cell => cell.column.id === col.id)!;

                                            const cellFromCol2 = table
                                                .getRowModel()
                                                .rows[1].getVisibleCells()
                                                .find(cell => cell.column.id === col.id)!;

                                            return (
                                                <div key={col.id} className={'flex gap-4'}>
                                                    <div className={'flex-1 font-medium'}>
                                                        {col.columnDef.header as ReactNode}
                                                    </div>
                                                    <div className={'flex-1'}>
                                                        {flexRender(
                                                            cellFromCol1.column.columnDef.cell,
                                                            cellFromCol1.getContext(),
                                                        )}
                                                    </div>
                                                    <div className={'flex-1'}>
                                                        {flexRender(
                                                            cellFromCol2.column.columnDef.cell,
                                                            cellFromCol2.getContext(),
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </DuplicateColumnContainer>
                                    <div className={'flex gap-2 self-end'}>
                                        <IgnoreButton.Root>
                                            <IgnoreButton.Button
                                                disabled={ignoreIsPending || mergeIsPending}
                                                onClick={() => ignoreDuplicate()}
                                            >
                                                {ignoreIsPending ? (
                                                    <>
                                                        <FontAwesomeIcon
                                                            className="animate-spin"
                                                            icon={faSpinnerThird}
                                                        />
                                                        Ignoring
                                                    </>
                                                ) : (
                                                    'Ignore'
                                                )}
                                            </IgnoreButton.Button>
                                            <IgnoreButton.AlertContent>
                                                <IgnoreButton.AlertBody>
                                                    <IgnoreButton.Title>
                                                        Ignore duplicates?
                                                    </IgnoreButton.Title>
                                                    <IgnoreButton.Description>
                                                        These records will remain as separate
                                                        contacts and retain all of their individual
                                                        data.
                                                    </IgnoreButton.Description>
                                                </IgnoreButton.AlertBody>
                                                <IgnoreButton.AlertAction
                                                    onClick={() => ignoreDuplicate()}
                                                />
                                            </IgnoreButton.AlertContent>
                                        </IgnoreButton.Root>

                                        <Form.UseFormSubmitButton
                                            isSubmitting={mergeIsPending}
                                            disabled={ignoreIsPending || mergeIsPending}
                                            className={
                                                'flex !h-9 items-center gap-1.5 !px-3 !py-2 !text-sm'
                                            }
                                        >
                                            {!mergeIsPending && <FontAwesomeIcon icon={faMerge} />}
                                            <p>Merge</p>
                                        </Form.UseFormSubmitButton>
                                    </div>
                                </div>
                            </form>
                        )}
                    </motion.div>
                </AnimatePresence>
            </Box>
        </Form.Provider>
    );
};

interface DuplicateColumnContainerProps extends ComponentPropsWithoutRef<'div'> {}
const DuplicateColumnContainer = (props: DuplicateColumnContainerProps) => {
    const { className = '', ...rest } = props;
    return (
        <div
            className={twMerge(
                'flex flex-col [&>*:nth-child(odd)]:bg-gray-100 [&>*]:rounded-md [&>*]:p-2',
                className,
            )}
            {...rest}
        />
    );
};

export default ContactDuplicates;
